import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, Variants, useScroll, useSpring, useTransform, AnimatePresence } from 'framer-motion';
import { FaWhatsapp, FaArrowUp } from 'react-icons/fa';
import Button from '../Button';
import { Icon } from './Icon';
import { imageProps, videoAsset, ImageName, SIZES } from './media';

// --- Kontakt- und Konversionsdaten (AP Clean Services) ---

/** Telefonnummer in E.164 - einzige Quelle fuer Tel-Link und WhatsApp-Nummer. */
const PHONE_E164 = '+491729182451';
/** Telefonnummer in der Schreibweise fuer die Anzeige. */
export const PHONE_DISPLAY = '+49 172 9182451';
/** href-Wert fuer Telefon-Links. */
export const PHONE_HREF = `tel:${PHONE_E164}`;

export const WHATSAPP_MESSAGE =
  'Hallo, ich habe Ihre Website gefunden und interessiere mich für eine Reinigung. Ich möchte gerne eine kostenlose Besichtigung anfragen.';
export const WHATSAPP_URL = `https://wa.me/${PHONE_E164.replace('+', '')}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

// Wiederverwendbarer WhatsApp-CTA (Sekundär-Aktion)
export const WhatsAppButton: React.FC<{ size?: 'sm' | 'md' | 'lg' }> = ({ size = 'lg' }) => (
  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
    <Button size={size} variant="outline">
      <Icon icon={FaWhatsapp} style={{ marginRight: 8, height: 20, width: 20 }} />
      Direkt per WhatsApp anfragen
    </Button>
  </a>
);

export const MotionCard = styled(motion.div)`
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border);
  background: var(--surface);
  margin-bottom: 2rem;
  transition: box-shadow 0.2s, transform 0.2s;
  will-change: transform;
  &:hover {
    box-shadow: var(--shadow-lg);
    transform: translateY(-4px);
  }
`;

// Standard Card für alle Bereiche außer Leistungen
export const Card = styled.div`
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border);
  background: var(--surface);
  margin-bottom: 2rem;
`;
export const CardHeader = styled.div`
  padding: 1.2rem;
  border-bottom: 1px solid var(--border);
`;
export const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
export const CardContent = styled.div`
  padding: 1.2rem;
  color: var(--ink-muted);
`;
export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border-radius: 999px;
  background: var(--brand-50);
  color: var(--brand);
  padding: 0.35em 1.05em;
  font-size: 0.95em;
  font-weight: 600;
  margin-bottom: 1rem;
`;
export const Section = styled.section<{ bg?: string }>`
  position: relative;
  padding: 4rem 0;
  background: ${props => props.bg || 'transparent'};
`;
export const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
`;

// Übergangs-Komponente.
// preserveAspectRatio="none" ist zwingend: ohne das skaliert der Browser den
// 1440x80-viewBox in die 60px hohe Box mit "meet" hinein, die Welle wird nur
// 1080px breit und links/rechts bleiben zwei harte Rechteck-Kanten stehen.
export const SectionDivider: React.FC<{ color?: string; flip?: boolean }> = ({ color = '#e6edfa', flip = false }) => (
  <svg
    viewBox="0 0 1440 80"
    preserveAspectRatio="none"
    aria-hidden="true"
    focusable="false"
    style={{ display: 'block', width: '100%', height: '60px', margin: '-1px 0', transform: flip ? 'scaleY(-1)' : 'none' }}
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M0,80 C480,0 960,160 1440,80 L1440,0 L0,0 Z" fill={color} />
  </svg>
);

// Animations-Objekt für Sektionen
export const sectionAnim = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
};

// Richtungs-Varianten für abwechslungsreiche Sektions-Übergänge
const sectionVariants: Record<'up' | 'left' | 'right' | 'scale', Variants> = {
  up: sectionAnim,
  left: { hidden: { opacity: 0, x: -70 }, visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } } },
  right: { hidden: { opacity: 0, x: 70 }, visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } } },
  scale: { hidden: { opacity: 0, scale: 0.92 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: 'easeOut' } } },
};

// Die Varianten "left" und "right" schieben die Sektion waehrend der
// Einblendung um 70px zur Seite. Ohne clippenden Wrapper waechst dadurch die
// Dokumentbreite ueber den Viewport hinaus. "clip" statt "hidden", weil
// "overflow-x: hidden" das vertikale overflow implizit auf "auto" setzt und
// damit Schatten und Sticky-Verhalten stoert.
const ClipX = styled.div`
  overflow-x: clip;
`;

// Wiederverwendbarer Wrapper für animierte Sektionen
export const AnimatedSection: React.FC<{
  children: React.ReactNode;
  once?: boolean;
  variant?: 'up' | 'left' | 'right' | 'scale';
}> = ({ children, once = false, variant = 'up' }) => {
  const section = (
    <motion.div
      variants={sectionVariants[variant]}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount: 0.2 }}
    >
      {children}
    </motion.div>
  );
  const horizontal = variant === 'left' || variant === 'right';
  return horizontal ? <ClipX>{section}</ClipX> : section;
};

// Branding-Styles und moderne Optik
export const GlobalBrandStyles: React.FC = () => (
  <style>{`
    body {
      background: linear-gradient(120deg, var(--brand-50) 0%, #fff 100%);
      font-family: 'Segoe UI', 'Roboto', Arial, sans-serif;
    }
    .btn {
      border-radius: var(--radius);
      font-weight: 500;
      transition: background .2s, color .2s, box-shadow .2s;
      box-shadow: 0 2px 8px rgba(0,52,196,0.07);
    }
    .btn--primary {
      background: var(--brand);
      color: #fff;
      border: none;
    }
    .btn--primary:hover {
      background: var(--brand-700);
    }
    .btn--outline {
      background: #fff;
      color: var(--brand);
      border: 2px solid var(--brand);
    }
    .btn--outline:hover {
      background: var(--brand-50);
    }
    .card {
      border-radius: var(--radius);
      box-shadow: 0 2px 16px rgba(0,52,196,0.08);
      border: 1px solid #e3eaf5;
      background: #fff;
    }
    .badge {
      background: var(--brand-50);
      color: var(--brand);
      border-radius: 999px;
      padding: 0.3em 1em;
      font-size: 0.95em;
      font-weight: 500;
    }
    .brand-title {
      letter-spacing: 1px;
    }
    .h1 {
      color: var(--brand);
    }
    .h2 {
      color: var(--brand);
    }
  `}</style>
);

// ========================================================================
// Visuelle Bausteine: Bilder, Deko-Formen & Animationen
// ========================================================================

// Marken-Verlauf als Fallback, falls ein Foto nicht lädt
const BRAND_FALLBACK = 'linear-gradient(135deg, var(--brand) 0%, #234be6 55%, #6f8df5 100%)';

const ImageFrame = styled.div<{ $radius?: string; $ratio?: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${props => props.$ratio || '16 / 10'};
  border-radius: ${props => props.$radius || '16px'};
  overflow: hidden;
  background: ${BRAND_FALLBACK};
  background-size: cover;
`;
const StyledFallbackImg = styled(motion.img)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

// Foto mit weichem Zoom-In beim Laden und Fallback auf Marken-Verlauf.
// `name` ist der Basisname aus assets/images, nicht der Dateipfad.
export const ImageWithFallback: React.FC<{
  name: ImageName;
  alt: string;
  sizes: string;
  radius?: string;
  ratio?: string;
  className?: string;
}> = ({ name, alt, sizes, radius, ratio, className }) => {
  const [failed, setFailed] = React.useState(false);
  return (
    <ImageFrame $radius={radius} $ratio={ratio} className={className}>
      {!failed && (
        <StyledFallbackImg
          {...imageProps(name, sizes)}
          alt={alt}
          onError={() => setFailed(true)}
          initial={{ opacity: 0, scale: 1.08 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        />
      )}
    </ImageFrame>
  );
};

// Animierter, langsam wandernder Marken-Verlauf (für Hero-Hintergründe)
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;
export const AnimatedGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, #e6edfa 0%, #f4f7fe 25%, #dbe6ff 50%, #eef3ff 75%, #e6edfa 100%);
  background-size: 300% 300%;
  animation: ${gradientShift} 18s ease infinite;
  z-index: 0;
`;

// Schwebende, weiche Deko-Formen im Hintergrund
const floatA = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(24px, -30px) scale(1.08); }
`;
const floatB = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-28px, 26px) scale(1.12); }
`;
const Blob = styled.div<{ $c: string; $s: number; $t: string; $l: string; $a: 'a' | 'b'; $d: string }>`
  position: absolute;
  width: ${props => props.$s}px;
  height: ${props => props.$s}px;
  top: ${props => props.$t};
  left: ${props => props.$l};
  background: ${props => props.$c};
  border-radius: 50%;
  filter: blur(38px);
  opacity: 0.5;
  animation: ${props => (props.$a === 'a' ? floatA : floatB)} ${props => props.$d} ease-in-out infinite;
  pointer-events: none;
`;
export const FloatingBlobs: React.FC = () => (
  <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0, pointerEvents: 'none' }}>
    <Blob $c="var(--brand)" $s={280} $t="-60px" $l="-40px" $a="a" $d="12s" />
    <Blob $c="#6f8df5" $s={220} $t="40%" $l="75%" $a="b" $d="15s" />
    <Blob $c="#a7c0ff" $s={180} $t="70%" $l="10%" $a="a" $d="17s" />
  </div>
);

// Wasserschwall – kräftige, sichtbare Wasser-Animation ("Eimer von den Seiten")
const dropFall = keyframes`
  0% { transform: translateY(-10%) scaleY(0.8); opacity: 0; }
  8% { opacity: 0.95; }
  85% { opacity: 0.95; }
  100% { transform: translateY(1400%) scaleY(1.1); opacity: 0; }
`;
const gushLeft = keyframes`
  0% { transform: translate(-60%, -40%) scale(0.6) rotate(-8deg); opacity: 0; }
  15% { opacity: 0.9; }
  60% { opacity: 0.85; }
  100% { transform: translate(40%, 260%) scale(1.15) rotate(6deg); opacity: 0; }
`;
const gushRight = keyframes`
  0% { transform: translate(60%, -40%) scale(0.6) rotate(8deg); opacity: 0; }
  15% { opacity: 0.9; }
  60% { opacity: 0.85; }
  100% { transform: translate(-40%, 260%) scale(1.15) rotate(-6deg); opacity: 0; }
`;
const Droplet = styled.div<{ $l: string; $d: string; $delay: string; $s: number }>`
  position: absolute;
  top: -30px;
  left: ${props => props.$l};
  width: ${props => props.$s}px;
  height: ${props => props.$s * 1.5}px;
  background: linear-gradient(180deg, rgba(111, 141, 245, 0.55) 0%, rgba(0, 52, 196, 0.85) 100%);
  border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
  box-shadow: 0 0 6px rgba(0, 52, 196, 0.35);
  animation: ${dropFall} ${props => props.$d} linear ${props => props.$delay} infinite;
  pointer-events: none;
`;
const WaterGush = styled.div<{ $side: 'left' | 'right'; $t: string; $d: string; $delay: string; $s: number }>`
  position: absolute;
  top: ${props => props.$t};
  ${props => (props.$side === 'left' ? 'left: -60px;' : 'right: -60px;')}
  width: ${props => props.$s}px;
  height: ${props => props.$s * 0.5}px;
  background: radial-gradient(ellipse at center, rgba(111, 141, 245, 0.75) 0%, rgba(0, 52, 196, 0.55) 55%, rgba(0, 52, 196, 0) 75%);
  border-radius: 50%;
  filter: blur(2px);
  animation: ${props => (props.$side === 'left' ? gushLeft : gushRight)} ${props => props.$d} ease-in ${props => props.$delay} infinite;
  pointer-events: none;
`;
export const FallingDroplets: React.FC = () => (
  <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0, pointerEvents: 'none' }}>
    {/* Wasserschwälle von den Seiten */}
    <WaterGush $side="left" $t="6%" $s={260} $d="3.2s" $delay="0s" />
    <WaterGush $side="left" $t="22%" $s={200} $d="3.8s" $delay="0.7s" />
    <WaterGush $side="left" $t="40%" $s={230} $d="3.4s" $delay="1.4s" />
    <WaterGush $side="right" $t="10%" $s={250} $d="3.5s" $delay="0.4s" />
    <WaterGush $side="right" $t="28%" $s={210} $d="3.1s" $delay="1.1s" />
    <WaterGush $side="right" $t="46%" $s={240} $d="3.9s" $delay="1.8s" />
    {/* Dichte, sichtbare Tropfen */}
    <Droplet $l="4%" $s={12} $d="2.6s" $delay="0s" />
    <Droplet $l="11%" $s={9} $d="3.1s" $delay="0.5s" />
    <Droplet $l="18%" $s={14} $d="2.3s" $delay="0.2s" />
    <Droplet $l="25%" $s={8} $d="3.4s" $delay="0.9s" />
    <Droplet $l="32%" $s={13} $d="2.5s" $delay="0.3s" />
    <Droplet $l="39%" $s={10} $d="3s" $delay="1.1s" />
    <Droplet $l="46%" $s={15} $d="2.2s" $delay="0.6s" />
    <Droplet $l="53%" $s={9} $d="3.3s" $delay="0.1s" />
    <Droplet $l="60%" $s={12} $d="2.7s" $delay="0.8s" />
    <Droplet $l="67%" $s={11} $d="2.4s" $delay="0.4s" />
    <Droplet $l="74%" $s={14} $d="3.1s" $delay="1s" />
    <Droplet $l="81%" $s={9} $d="2.6s" $delay="0.2s" />
    <Droplet $l="88%" $s={13} $d="2.9s" $delay="0.7s" />
    <Droplet $l="95%" $s={10} $d="2.4s" $delay="1.2s" />
  </div>
);

// Wasser-Video-Overlay (echtes Wasser, per Blend-Mode ohne Alphakanal).
// Bewusst nur EIN Videoelement ueber die volle Breite: die frueheren zwei
// gespiegelten Haelften haben das Video doppelt geladen und in der Mitte
// eine sichtbare Kante erzeugt.
const WaterVideoEl = styled.video`
  position: absolute;
  inset: 0;
  height: 100%;
  width: 100%;
  object-fit: cover;
  mix-blend-mode: screen;
  opacity: 0.9;
  pointer-events: none;
`;
const WaterPoster = styled.div<{ $poster: string }>`
  position: absolute;
  inset: 0;
  background: url(${props => props.$poster}) center / cover no-repeat;
  mix-blend-mode: screen;
  opacity: 0.9;
  pointer-events: none;
`;

/** Respektiert die Systemeinstellung "Bewegung reduzieren". */
function usePrefersReducedMotion(): boolean {
  // Lazy-Initializer statt Startwert false: sonst wird das Video einmal
  // gemountet, der Download startet, und erst der Effekt nach dem Paint
  // entfernt es wieder.
  const [reduced, setReduced] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
}

export const WaterOverlay: React.FC<{ name: Parameters<typeof videoAsset>[0] }> = ({ name }) => {
  const reducedMotion = usePrefersReducedMotion();
  const { src, poster } = videoAsset(name);

  // Das Video wird bewusst erst nach dem Mounten gerendert. Der Prerender-
  // Schnappschuss enthaelt sonst ein fertiges <video>-Tag, und der Browser
  // beginnt den Download, bevor React ueberhaupt hydriert - auch bei
  // "Bewegung reduzieren", wo es danach sofort wieder entfernt wuerde.
  // Bis dahin steht das Poster-Standbild (23 kB) an derselben Stelle.
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0, pointerEvents: 'none' }}>
      {mounted && !reducedMotion ? (
        <WaterVideoEl src={src} poster={poster} autoPlay loop muted playsInline preload="metadata" />
      ) : (
        <WaterPoster $poster={poster} />
      )}
    </div>
  );
};

// Hero-Medienbild mit Verlaufs-Overlay (für die Detailseiten-Hero)
const HeroMediaWrap = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
`;
const HeroMediaImg = styled(motion.img)`
  position: absolute;
  top: -8%;
  left: 0;
  width: 100%;
  height: 116%;
  object-fit: cover;
`;
const HeroMediaOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, rgba(0,52,196,0.88) 0%, rgba(35,75,230,0.72) 45%, rgba(0,52,196,0.55) 100%);
`;
export const HeroMedia: React.FC<{ name: ImageName; alt: string }> = ({ name, alt }) => {
  const [failed, setFailed] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  return (
    <HeroMediaWrap ref={wrapRef}>
      {!failed && (
        <HeroMediaImg
          {...imageProps(name, SIZES.hero)}
          // Hero-Bild ist der wahrscheinlichste LCP-Kandidat der Detailseiten
          // und steht sofort im Viewport - deshalb eager statt lazy.
          loading="eager"
          alt={alt}
          style={{ y }}
          onError={() => setFailed(true)}
          initial={{ scale: 1.12, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
      )}
      <HeroMediaOverlay />
    </HeroMediaWrap>
  );
};

// Wiederverwendbare Reveal-Varianten (gestaffelt)
export const staggerContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};
export const revealUp: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};
export const revealLeft: Variants = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};
export const revealRight: Variants = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};
export const revealScale: Variants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: 'easeOut' } },
};

// Globale Scroll-Fortschrittsleiste (oben fixiert, füllt sich beim Runterscrollen)
const ProgressBar = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  transform-origin: 0%;
  background: linear-gradient(90deg, var(--brand) 0%, #234be6 50%, #6f8df5 100%);
  z-index: 1000;
`;
export const ScrollProgress: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  return <ProgressBar style={{ scaleX }} />;
};

// "Nach oben"-Button, erscheint animiert nach etwas Scrollen
const ScrollTopBtn = styled(motion.button)`
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, var(--brand) 0%, #234be6 55%, #6f8df5 100%);
  box-shadow: 0 8px 22px rgba(0, 52, 196, 0.35);
  z-index: 999;
`;
export const ScrollToTopButton: React.FC = () => {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <AnimatePresence>
      {visible && (
        <ScrollTopBtn
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Nach oben scrollen"
          initial={{ opacity: 0, scale: 0.5, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.5, y: 20 }}
          whileHover={{ scale: 1.12, y: -2 }}
          whileTap={{ scale: 0.92 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
        >
          <Icon icon={FaArrowUp} size={18} />
        </ScrollTopBtn>
      )}
    </AnimatePresence>
  );
};
